/** Click-to-call: ring someone, then open the room on the caller's side. */
import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import { callsApi, type PlaceCallInput } from '../data/callsApi';
import { activeCallActions } from '../store/activeCallStore';
import { useOpenCall } from './useOpenCall';

export interface PlaceCallRequest extends PlaceCallInput {
  /** Shown on the ringing screen before the SFU roster exists. */
  readonly peerName: string;
}

export interface PlaceCallApi {
  readonly isPending: boolean;
  place(request: PlaceCallRequest): void;
}

export function usePlaceCall(): PlaceCallApi {
  const openCall = useOpenCall();

  const mutation = useMutation({
    mutationFn: (request: PlaceCallRequest) => callsApi.place(request),
    onSuccess: (response, request) => {
      activeCallActions.set({
        callId: response.call.id,
        credentials: response.credentials,
        kind: response.call.kind,
        direction: 'outgoing',
        peerName: request.peerName,
        peerId: request.calleeIds[0] ?? null,
        withVideo: response.call.kind === 'video',
        connected: false,
      });
      /* In a browser: this document, the way Teams' own web client does it.
         In the desktop shell: a window of its own, the way Teams' desktop
         client does it, with the workspace left exactly where it was.
         `startsConnected: false` is what opens it on "Ringing..." — the window
         re-mints its grant from `POST /calls/:id/join`, which cannot tell an
         unanswered outgoing call from a rejoin. */
      openCall({
        callId: response.call.id,
        peerName: request.peerName,
        kind: response.call.kind,
        startsConnected: false,
      });
    },
    onError: () => toast.error('Could not start the call. Try again.'),
  });

  return {
    isPending: mutation.isPending,
    place: useCallback((request: PlaceCallRequest) => mutation.mutate(request), [mutation]),
  };
}
